import type { RouteEntry } from '../types';

// ═══════════════════════════════════════════════════════════════════════════
// ACTUARIAL MODEL
// ═══════════════════════════════════════════════════════════════════════════
export const ACTUARIAL_MODEL_ROUTES: RouteEntry[] = [
  {
    path: 'actuarial-model',
    component: 'ActuarialModelDashboard',
    label: 'Dashboard',
    allowedRoles: [], // no ProtectedRoute wrapper — function dashboard
    functionality: 'actuarial_model',
    navSection: 'Overview',
    navIcon: 'Dashboard',
    // MISMATCH: route has no role guard but should be consultant_user + admin only per domain rules
  },
  {
    path: 'act-method-selection',
    component: 'MethodSelectionPage',
    label: 'Method Selection',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'actuarial_model',
    navSection: 'Model',
    navIcon: 'Calculate',
  },
  {
    path: 'act-stochastic',
    component: 'StochasticReservingPage',
    label: 'Stochastic Reserving',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'actuarial_model',
    navSection: 'Model',
    navIcon: 'ShowChart',
  },
  {
    path: 'act-experience',
    component: 'ExperienceAnalysisPage',
    label: 'Experience Analysis',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'actuarial_model',
    navSection: 'Model',
    navIcon: 'TrendingDown',
  },
  {
    path: 'act-governance',
    component: 'AssumptionGovernancePage',
    label: 'Assumption Governance',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'actuarial_model',
    navSection: 'Company Assumptions',
    navIcon: 'Gavel',
  },
  // Nav-only: life-assumptions also appears in actuarial_model Company Assumptions
  {
    path: 'life-assumptions',
    component: 'LifeAssumptionsPage',
    label: 'Life Assumptions',
    allowedRoles: [],
    functionality: 'actuarial_model',
    navSection: 'Company Assumptions',
    navIcon: 'GridOn',
    navOrder: 2,
  },
  {
    path: 'act-active-products',
    component: 'ActActiveProductsPage',
    label: 'Active Products',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'actuarial_model',
    navSection: 'Company Assumptions',
    navIcon: 'Category',
    navRoles: ['consultant_user', 'super_admin'],
    navOrder: 3,
  },
  {
    path: 'act-linkage',
    component: 'ProductLinkagePage',
    label: 'Product Linkages',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'actuarial_model',
    navSection: 'Company Assumptions',
    navIcon: 'Link',
    navRoles: ['consultant_user', 'super_admin'],
    navOrder: 4,
  },
  {
    path: 'act-engine-manual',
    component: 'EngineManualPage',
    label: '',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'actuarial_model',
    hideFromNav: true,
    // MISMATCH: route exists but no nav item
  },
  {
    path: 'act-gmm-input',
    component: 'GMMPolicyInputPage',
    label: '',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'actuarial_model',
    hideFromNav: true,
    // MISMATCH: route exists but no nav item (GMM Policy Input is a sub-page)
  },
  // Nav-only: admin routes that appear in actuarial_model Global Assumptions
  {
    path: 'admin/yield-curves',
    component: 'AdminYieldCurvesPage',
    label: 'Yield Curves',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'actuarial_model',
    navSection: 'Global Assumptions',
    navIcon: 'ShowChart',
  },
  {
    path: 'admin/mortality-tables',
    component: 'AdminMortalityTablesPage',
    label: 'Mortality Tables',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'actuarial_model',
    navSection: 'Global Assumptions',
    navIcon: 'GridOn',
  },
  // Nav-only: triangles also appears in actuarial_model Data section
  {
    path: 'triangles',
    component: 'TrianglesPage',
    label: 'Triangles',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'actuarial_model',
    navSection: 'Data',
    navIcon: 'GridOn',
  },
  {
    path: 'act-gmm-test',
    component: 'GMMTestRunnerPage',
    label: 'GMM Test Runner',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'actuarial_model',
    navSection: 'GMM / VFA Model',
    navIcon: 'PlayArrow',
    navRoles: ['consultant_user', 'super_admin', 'admin'],
    navCollapsible: true,
  },
  {
    path: 'act-gmm-comparison',
    component: 'GMMComparisonPage',
    label: 'Results Comparison',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'actuarial_model',
    navSection: 'GMM / VFA Model',
    navIcon: 'CompareArrows',
    navRoles: ['consultant_user', 'super_admin', 'admin'],
  },
  // Nav-only: routes that appear in actuarial_model Results section
  {
    path: 'reserve-results',
    component: 'ReserveResultsPage',
    label: 'Reserve Results',
    allowedRoles: [],
    functionality: 'actuarial_model',
    navSection: 'Results',
    navIcon: 'AccountBalanceWallet',
    navOrder: 1,
  },
  {
    path: 'calculations',
    component: 'CalculationsPage',
    label: 'Calculations',
    allowedRoles: ['super_admin', 'admin', 'consultant_user', 'company_user', 'auditor', 'executive'],
    functionality: 'actuarial_model',
    navSection: 'Results',
    navIcon: 'Calculate',
    navRoles: ['consultant_user'],
    navOrder: 2,
  },
  {
    path: 'life-calculations',
    component: 'LifeCalculationPage',
    label: 'Life Calculations',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'actuarial_model',
    navSection: 'Results',
    navIcon: 'Calculate',
    navRoles: ['consultant_user'],
    navOrder: 3,
  },
  {
    path: 'life-disclosures',
    component: 'LifeDisclosuresPage',
    label: 'Life Disclosures',
    allowedRoles: ['company_user', 'consultant_user', 'auditor', 'regulator', 'executive'],
    functionality: 'actuarial_model',
    navSection: 'Results',
    navIcon: 'Article',
    navOrder: 4,
  },
  {
    path: 'life-contract-groups',
    component: 'LifeContractGroupsPage',
    label: 'Life Contract Groups',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'actuarial_model',
    navSection: 'Results',
    navIcon: 'AccountTree',
    navOrder: 5,
  },
  {
    path: 'life-reconciliation',
    component: 'LifeReconciliationPage',
    label: 'Life Reconciliation',
    allowedRoles: ['company_user', 'consultant_user', 'auditor', 'regulator', 'executive'],
    functionality: 'actuarial_model',
    navSection: 'Results',
    navIcon: 'TableChart',
    navOrder: 6,
  },
  {
    path: 'life-sensitivity',
    component: 'LifeSensitivityPage',
    label: 'Life Sensitivity',
    allowedRoles: ['company_user', 'consultant_user', 'auditor', 'regulator', 'executive'],
    functionality: 'actuarial_model',
    navSection: 'Results',
    navIcon: 'ShowChart',
    navOrder: 7,
  },
  {
    path: 'life-asset-share',
    component: 'LifeAssetSharePage',
    label: 'Asset Share (VFA)',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'actuarial_model',
    navSection: 'Results',
    navIcon: 'PieChart',
    navOrder: 8,
  },
  {
    path: 'act-csm-rollforward',
    component: 'CSMRollForwardPage',
    label: 'CSM Roll-Forward',
    allowedRoles: ['consultant_user', 'super_admin', 'admin'],
    functionality: 'actuarial_model',
    navSection: 'Results',
    navIcon: 'Timeline',
    navRoles: ['consultant_user', 'super_admin', 'admin'],
    navOrder: 9,
  },
  {
    path: 'act-risk-aggregation',
    component: 'RiskAggregationPage',
    label: 'Risk Aggregation',
    allowedRoles: ['consultant_user', 'super_admin'],
    functionality: 'actuarial_model',
    navSection: 'Results',
    navIcon: 'Hub',
    navRoles: ['consultant_user', 'super_admin'],
    navOrder: 10,
  },
];
